import React, { useState } from 'react';
import axios from 'axios';
import { Lock, CheckCircle2, ArrowLeft, ArrowRight, Rocket, AlertCircle, MailCheck } from 'lucide-react';
import { INDUSTRY_GROUPS, CAMPAIGN_GOALS, US_STATES } from '../mock';
import SuccessScreen from './SuccessScreen';

const API_BASE = `${process.env.REACT_APP_BACKEND_URL}/api`;

const initial = {
  businessName: '',
  ownerName: '',
  industry: '',
  websiteUrl: '',
  city: '',
  state: '',
  phone: '',
  email: '',
  referralSource: 'LinkedIn',
  goals: [],
  otherGoal: '',
  planChoice: 'trial',
  consents: { canspam: false, dataOwnership: false, contentAuthority: false },
};

const STEP_LABELS = ['Business', 'Audience', 'Plan', 'Consent', 'Review'];

export default function IntakeForm() {
  const [step, setStep] = useState(1);
  const [data, setData] = useState(initial);
  const [errors, setErrors] = useState({});
  const [submitted, setSubmitted] = useState(false);
  const [submitData, setSubmitData] = useState(null);
  const [submitError, setSubmitError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  if (submitted) return <SuccessScreen data={submitData || data} />;

  const set = (k, v) => setData((d) => ({ ...d, [k]: v }));

  const validateStep1 = () => {
    const e = {};
    if (!data.businessName.trim()) e.businessName = 'Please enter your business name.';
    if (!data.ownerName.trim()) e.ownerName = 'Please enter your name.';
    if (!data.industry) e.industry = 'Please select your industry.';
    if (!data.websiteUrl.trim()) e.websiteUrl = 'Please enter your website URL.';
    if (!data.city.trim()) e.city = 'Please enter your city.';
    if (!data.state) e.state = 'Please select your state.';
    if (!data.phone.trim()) e.phone = 'Please enter your phone number.';
    if (!/^\S+@\S+\.\S+$/.test(data.email)) e.email = 'Please enter a valid email address.';
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const next = () => {
    if (step === 1 && !validateStep1()) return;
    setErrors({});
    setStep((s) => Math.min(5, s + 1));
  };
  const back = () => {
    setErrors({});
    setStep((s) => Math.max(1, s - 1));
  };

  const allConsents = Object.values(data.consents).every(Boolean);

  const toggleGoal = (g) => {
    setData((d) => ({
      ...d,
      goals: d.goals.includes(g) ? d.goals.filter((x) => x !== g) : [...d.goals, g],
    }));
  };

  const submit = async () => {
    if (!allConsents) return;
    setSubmitting(true);
    setSubmitError('');
    const payload = {
      business_name: data.businessName.trim(),
      owner_name: data.ownerName.trim(),
      industry: data.industry,
      website_url: data.websiteUrl.trim(),
      city_state: `${data.city.trim()}, ${data.state}`,
      phone: data.phone.trim(),
      email: data.email.trim(),
      referral_source: data.referralSource,
      goals: data.goals,
      other_goal: data.otherGoal.trim(),
      audience: '',
      csv_uploaded: false,
      leads_addon: false,
      plan_choice: 'trial',
      selected_campaigns: [],
      consent_canspam: data.consents.canspam,
      consent_tcpa: false,
      consent_data_ownership: data.consents.dataOwnership,
      consent_content_authority: data.consents.contentAuthority,
    };
    try {
      const res = await axios.post(`${API_BASE}/submissions`, payload);
      setSubmitData({ ...res.data, email: data.email });
      setSubmitted(true);
    } catch (err) {
      const msg = err?.response?.data?.detail || err?.message || 'Something went wrong. Please try again.';
      setSubmitError(typeof msg === 'string' ? msg : 'Something went wrong. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div id="intake-form" className="relative">
      {/* Q4 Launch overlay: visually blocks form, keeps DOM + automations intact */}
      <div
        aria-hidden="false"
        role="status"
        data-testid="intake-form-launching-overlay"
        className="absolute inset-0 z-30 flex items-center justify-center rounded-2xl"
        style={{
          backgroundColor: 'rgba(15, 14, 26, 0.55)',
          backdropFilter: 'blur(6px)',
          WebkitBackdropFilter: 'blur(6px)',
        }}
      >
        <div
          data-testid="intake-form-launching-badge"
          className="px-5 py-3 rounded-full text-sm sm:text-base font-semibold tracking-wide inline-flex items-center gap-2 shadow-[0_12px_30px_-8px_rgba(19,15,40,0.55)] text-center"
          style={{
            backgroundColor: '#FAFAF9',
            color: '#130F28',
            border: '1px solid rgba(107,75,239,0.35)',
            maxWidth: '90%',
          }}
        >
          <Lock size={16} aria-hidden="true" style={{ color: '#6B4BEF' }} />
          <span>Launching Q4, 2026 · First 1,000 businesses get Year 1 free</span>
        </div>
      </div>

      <div
        data-testid="intake-form-disabled-wrapper"
        aria-hidden="true"
        tabIndex={-1}
        className="rounded-2xl overflow-hidden bg-white shadow-[0_24px_60px_-20px_rgba(19,15,40,0.25)] border border-[#ece8df] pointer-events-none select-none"
        style={{ filter: 'grayscale(0.15)' }}
      >
      {/* Header */}
      <div className="px-5 sm:px-6 pt-5 sm:pt-6 pb-5" style={{ backgroundColor: '#0f0e1a', color: '#FAFAF9' }}>
        <h2 className="font-serif text-xl sm:text-2xl leading-tight">Start your Year 1 free</h2>
        <p className="text-xs sm:text-sm mt-1 text-white/70">First 1,000 businesses to sign up. No credit card required.</p>
      </div>

      {/* Progress */}
      <nav aria-label="Form progress" className="px-4 sm:px-6 py-3 sm:py-4 border-b border-[#ece8df] bg-[#fbfaf6]">
        {/* Mobile: numbered dots + active label */}
        <div className="flex items-center justify-between gap-2 sm:hidden">
          <ol className="flex items-center gap-1.5" aria-hidden="true">
            {STEP_LABELS.map((label, i) => {
              const n = i + 1;
              const active = step === n;
              const done = step > n;
              const bg = active ? '#6B4BEF' : done ? '#10C98F' : 'transparent';
              const color = active || done ? '#fff' : '#6b6685';
              const border = !active && !done ? '1.5px solid #cbc6b7' : 'none';
              return (
                <li key={label}>
                  <span className="w-6 h-6 rounded-full text-[11px] font-semibold flex items-center justify-center" style={{ backgroundColor: bg, color, border }}>
                    {done ? '✓' : n}
                  </span>
                </li>
              );
            })}
          </ol>
          <p className="text-xs font-semibold truncate" style={{ color: '#130F28' }}>
            Step {step} of {STEP_LABELS.length}: {STEP_LABELS[step - 1]}
          </p>
        </div>

        {/* Tablet / desktop: full progress with labels */}
        <ol className="hidden sm:flex items-center justify-between gap-1">
          {STEP_LABELS.map((label, i) => {
            const n = i + 1;
            const active = step === n;
            const done = step > n;
            const bg = active ? '#6B4BEF' : done ? '#10C98F' : 'transparent';
            const color = active || done ? '#fff' : '#6b6685';
            const border = !active && !done ? '1.5px solid #cbc6b7' : 'none';
            return (
              <li key={label} className="flex items-center gap-2 flex-1 min-w-0" aria-current={active ? 'step' : undefined}>
                <div className="flex items-center gap-2 min-w-0">
                  <span
                    className="w-7 h-7 rounded-full text-xs font-semibold flex items-center justify-center flex-shrink-0"
                    style={{ backgroundColor: bg, color, border }}
                    aria-hidden="true"
                  >
                    {done ? '✓' : n}
                  </span>
                  <span className={`text-xs font-medium truncate ${active ? 'text-[#130F28]' : 'text-[#6b6685]'}`}>
                    <span className="sr-only">Step {n}: </span>{label}
                  </span>
                </div>
                {n < STEP_LABELS.length && <span className="flex-1 h-px bg-[#e6e2d6]" aria-hidden="true" />}
              </li>
            );
          })}
        </ol>
      </nav>

      <div className="p-5 sm:p-6 fade-up" key={step}>
        {step === 1 && (
          <fieldset className="space-y-4">
            <legend className="font-serif text-xl mb-1" style={{ color: '#130F28' }}>Step 1: Business information</legend>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Field id="businessName" label="Business name" required error={errors.businessName}>
                <input id="businessName" className="field-input" placeholder="Apex HVAC & Plumbing"
                  value={data.businessName} onChange={(e) => set('businessName', e.target.value)}
                  aria-required="true" aria-invalid={!!errors.businessName} />
              </Field>
              <Field id="ownerName" label="Owner name" required error={errors.ownerName}>
                <input id="ownerName" className="field-input" placeholder="Sarah Johnson"
                  value={data.ownerName} onChange={(e) => set('ownerName', e.target.value)}
                  aria-required="true" aria-invalid={!!errors.ownerName} />
              </Field>
            </div>

            <Field id="industry" label="Industry" required error={errors.industry}>
              <select id="industry" className="field-input" value={data.industry}
                onChange={(e) => set('industry', e.target.value)}
                aria-required="true" aria-invalid={!!errors.industry}>
                <option value="">Select your industry…</option>
                {INDUSTRY_GROUPS.map((g) => (
                  <optgroup key={g.label} label={g.label}>
                    {g.options.map((o) => <option key={o} value={o}>{o}</option>)}
                  </optgroup>
                ))}
              </select>
            </Field>

            <Field id="websiteUrl" label="Website URL" required error={errors.websiteUrl}>
              <input id="websiteUrl" type="url" className="field-input" placeholder="https://yourwebsite.com"
                value={data.websiteUrl} onChange={(e) => set('websiteUrl', e.target.value)}
                aria-required="true" aria-invalid={!!errors.websiteUrl} />
            </Field>

            <div className="grid grid-cols-1 sm:grid-cols-[2fr_1fr] gap-4">
              <Field id="city" label="City" required error={errors.city}>
                <input id="city" className="field-input" placeholder="Charleston"
                  value={data.city} onChange={(e) => set('city', e.target.value)}
                  autoComplete="address-level2"
                  aria-required="true" aria-invalid={!!errors.city} />
              </Field>
              <Field id="state" label="State" required error={errors.state}>
                <select id="state" className="field-input" value={data.state}
                  onChange={(e) => set('state', e.target.value)}
                  autoComplete="address-level1"
                  aria-required="true" aria-invalid={!!errors.state}>
                  <option value="">Select…</option>
                  {US_STATES.map((s) => (
                    <option key={s.code} value={s.code}>{s.code} - {s.name}</option>
                  ))}
                </select>
              </Field>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Field id="phone" label="Phone number" required error={errors.phone}>
                <input id="phone" type="tel" className="field-input" placeholder="(843) 555-0100"
                  value={data.phone} onChange={(e) => set('phone', e.target.value)}
                  aria-required="true" aria-invalid={!!errors.phone} />
              </Field>
              <Field id="email" label="Business email" required error={errors.email}>
                <input id="email" type="email" className="field-input" placeholder="sarah@apexhvac.com"
                  value={data.email} onChange={(e) => set('email', e.target.value)}
                  aria-required="true" aria-invalid={!!errors.email} />
              </Field>
            </div>

            <Field id="referralSource" label="How did you hear about Crush Marketing?">
              <select id="referralSource" className="field-input"
                value={data.referralSource} onChange={(e) => set('referralSource', e.target.value)}>
                <option value="LinkedIn">LinkedIn</option>
                <option value="Referred by someone">Referred by someone</option>
                <option value="Google/Search">Google/Search</option>
                <option value="Another website or online source">Another website or online source</option>
                <option value="Other">Other</option>
              </select>
            </Field>

            <div className="mt-4 p-4 rounded-xl text-sm leading-relaxed" style={{ backgroundColor: '#f2efe5', color: '#130F28' }}>
              <p><span className="font-semibold" style={{ color: '#5132d4' }}>Year 1 free</span> for the first 1,000 businesses to sign up. After year one, pricing is expected to start around $299/month. Cancel anytime.</p>
              <span className="inline-flex items-center gap-1 mt-2 text-xs font-semibold px-2.5 py-1 rounded-full" style={{ backgroundColor: 'rgba(16,201,143,0.18)', color: '#0a5c43' }}>
                <Lock size={12} aria-hidden="true" /> Year 1 free · No credit card required
              </span>
            </div>

            <div className="pt-2 flex justify-end">
              <button type="button" className="btn-primary rounded-full px-5 py-2.5 font-semibold inline-flex items-center gap-2" onClick={next}>
                Continue: Step 2 of 5 <ArrowRight size={16} aria-hidden="true" />
              </button>
            </div>
          </fieldset>
        )}

        {step === 2 && (
          <fieldset className="space-y-5">
            <legend className="font-serif text-xl mb-1" style={{ color: '#130F28' }}>Step 2: Audience</legend>

            <div role="group" aria-labelledby="goals-label">
              <p id="goals-label" className="text-sm font-semibold" style={{ color: '#130F28' }}>What are your main marketing goals? <RequiredMark/></p>
              <p className="text-xs mt-0.5" style={{ color: '#6b6685' }}>Select all that apply.</p>
              <div className="mt-3 flex flex-wrap gap-2">
                {CAMPAIGN_GOALS.map((g) => {
                  const on = data.goals.includes(g);
                  return (
                    <button key={g} type="button" onClick={() => toggleGoal(g)}
                      aria-pressed={on}
                      className="chip text-sm px-3.5 py-1.5 rounded-full border focus:outline-none focus:ring-2 focus:ring-offset-2"
                      style={{ backgroundColor: on ? '#6B4BEF' : '#fff', borderColor: on ? '#6B4BEF' : '#bdb8a3', color: on ? '#fff' : '#1f1c33' }}>
                      {g}
                    </button>
                  );
                })}
              </div>
              {data.goals.includes('Other') && (
                <input className="field-input mt-3" placeholder="Other marketing goal"
                  aria-label="Other marketing goal"
                  value={data.otherGoal} onChange={(e) => set('otherGoal', e.target.value)} />
              )}
            </div>

            <div className="pt-2 flex justify-between">
              <BackButton onClick={back} />
              <button type="button" className="btn-primary rounded-full px-5 py-2.5 font-semibold inline-flex items-center gap-2" onClick={next}>
                Continue: Step 3 of 5 <ArrowRight size={16} aria-hidden="true" />
              </button>
            </div>
          </fieldset>
        )}

        {step === 3 && (
          <fieldset className="space-y-5">
            <legend className="font-serif text-xl mb-1" style={{ color: '#130F28' }}>Step 3: Try it Out</legend>
            <p className="text-sm" style={{ color: '#3b3656' }}>
              Every account starts with Try it Out. Here's what you're signing up for.
            </p>

            <div className="rounded-2xl p-5 sm:p-6 border-2" style={{ borderColor: '#6B4BEF', backgroundColor: 'rgba(107,75,239,0.06)', boxShadow: '0 12px 30px -12px rgba(107,75,239,0.35)' }}>
              <span className="inline-flex items-center gap-1.5 text-[11px] sm:text-xs font-semibold px-2.5 py-1 rounded-full" style={{ backgroundColor: 'rgba(16,201,143,0.18)', color: '#0a5c43' }}>
                <Lock size={12} aria-hidden="true" /> Try it Out · First 7 days
              </span>
              <p className="font-serif text-lg sm:text-xl mt-3" style={{ color: '#130F28' }}>See exactly what your customers will see</p>
              <p className="mt-1">
                <span className="font-serif text-2xl sm:text-3xl" style={{ color: '#130F28' }}>$0</span>
                <span className="text-sm ml-1" style={{ color: '#4a4566' }}>for your first 7 days</span>
              </p>
              <ul className="mt-3 space-y-1.5 text-[13px]" style={{ color: '#1f1c33' }}>
                {[
                  'Your Nurture & Referral Campaign email arrives in your inbox within 48 hours.',
                  'Written for your business, built by AI agents checking each other.',
                  'Nothing goes to your customer list until you say so.',
                ].map((l) => (
                  <li key={l} className="flex gap-2">
                    <span aria-hidden="true" className="mt-1 flex-shrink-0 w-1.5 h-1.5 rounded-full" style={{ backgroundColor: '#6B4BEF' }} />
                    <span>{l}</span>
                  </li>
                ))}
              </ul>
            </div>

            <div className="p-4 rounded-xl fade-up" style={{ backgroundColor: 'rgba(16,201,143,0.10)', border: '1px solid rgba(16,201,143,0.35)' }}>
              <p className="text-sm flex items-start gap-2" style={{ color: '#0a5c43' }}>
                <MailCheck size={16} aria-hidden="true" className="mt-0.5 flex-shrink-0" />
                <span>
                  <span className="font-semibold">You're all set.</span> After you submit, you'll receive a welcome email within 24 hours walking you through the next steps.
                </span>
              </p>
            </div>

            <div className="pt-2 flex justify-between">
              <BackButton onClick={back} />
              <button type="button" className="btn-primary rounded-full px-5 py-2.5 font-semibold inline-flex items-center gap-2" onClick={next}>
                Continue: Step 4 of 5 <ArrowRight size={16} aria-hidden="true" />
              </button>
            </div>
          </fieldset>
        )}

        {step === 4 && (
          <fieldset className="space-y-4">
            <legend className="font-serif text-xl mb-1" style={{ color: '#130F28' }}>Step 4: Authorizations and consents</legend>
            <p className="text-sm" style={{ color: '#3b3656' }}>Please review and check all three authorizations to continue. All boxes required.</p>

            {[
              { key: 'canspam', title: 'CAN-SPAM Email Authorization', body: 'I authorize Crush Marketing to send commercial email campaigns on behalf of my business. I confirm that all email recipients have opted in or are existing customers as defined under CAN-SPAM guidelines.' },
              { key: 'dataOwnership', title: 'Data Ownership', body: 'I confirm that I own or have the legal right to use all customer data I upload. I understand Crush Marketing will use this data solely for marketing campaigns on my behalf and will not sell or share it with third parties.' },
              { key: 'contentAuthority', title: 'Content Authority', body: "I authorize Crush Marketing's team to write and schedule marketing content on behalf of my business. I understand all content will be reviewed by our team for compliance and brand standards before going live." },
            ].map((c) => {
              const on = data.consents[c.key];
              return (
                <label key={c.key} className="flex items-start gap-3 p-4 rounded-xl cursor-pointer transition"
                  style={{ backgroundColor: on ? 'rgba(16,201,143,0.10)' : '#fbfaf6', border: `1px solid ${on ? '#10C98F' : '#e6e2d6'}` }}>
                  <input type="checkbox" checked={on}
                    className="mt-1 w-4 h-4 accent-[#10C98F]"
                    aria-describedby={`consent-${c.key}-body`}
                    onChange={(e) => setData((d) => ({ ...d, consents: { ...d.consents, [c.key]: e.target.checked } }))} />
                  <span className="text-sm" style={{ color: '#130F28' }}>
                    <span className="font-semibold block mb-0.5">{c.title}</span>
                    <span id={`consent-${c.key}-body`}>{c.body}</span>
                  </span>
                </label>
              );
            })}

            <div className="pt-2 flex justify-between items-center">
              <BackButton onClick={back} />
              <button type="button" disabled={!allConsents}
                aria-disabled={!allConsents}
                className="btn-primary rounded-full px-5 py-2.5 font-semibold inline-flex items-center gap-2" onClick={next}>
                Continue: Step 5 of 5 <ArrowRight size={16} aria-hidden="true" />
              </button>
            </div>
          </fieldset>
        )}

        {step === 5 && (
          <fieldset className="space-y-4">
            <legend className="font-serif text-xl mb-1" style={{ color: '#130F28' }}>Step 5: Review your information</legend>

            <Section title="Business info" onEdit={() => setStep(1)}>
              <Row k="Business" v={data.businessName || '-'} />
              <Row k="Owner" v={data.ownerName || '-'} />
              <Row k="Industry" v={data.industry || '-'} />
              <Row k="Website" v={data.websiteUrl || '-'} />
              <Row k="Location" v={data.city && data.state ? `${data.city}, ${data.state}` : '-'} />
              <Row k="Phone" v={data.phone || '-'} />
              <Row k="Email" v={data.email || '-'} />
              <Row k="Heard about us" v={data.referralSource || '-'} />
            </Section>

            <Section title="Audience" onEdit={() => setStep(2)}>
              <Row k="Goals" v={data.goals.length ? data.goals.join(', ') : '-'} />
            </Section>

            <Section title="Plan" onEdit={() => setStep(3)}>
              <Row k="Plan" v="Year 1 Free (first 1,000 businesses)" />
              <Row k="Try it Out" v="A real campaign live in your first 7 days" />
            </Section>

            <Section title="Consents" onEdit={() => setStep(4)}>
              {[
                ['CAN-SPAM', data.consents.canspam],
                ['Data Ownership', data.consents.dataOwnership],
                ['Content Authority', data.consents.contentAuthority],
              ].map(([label, ok]) => (
                <div key={label} className="flex items-center gap-2 text-sm">
                  <CheckCircle2 size={16} style={{ color: ok ? '#10C98F' : '#cbc6b7' }} aria-hidden="true" />
                  <span style={{ color: '#130F28' }}>{label}</span>
                </div>
              ))}
            </Section>

            <div className="p-4 rounded-xl text-sm" style={{ backgroundColor: '#f2efe5', color: '#130F28' }}>
              <p><span className="font-semibold" style={{ color: '#5132d4' }}>Year 1 free</span> for the first 1,000 businesses. After year one, pricing is expected to start around $299/month. Cancel anytime.</p>
            </div>

            {submitError && (
              <div role="alert" className="flex items-start gap-2 p-3 rounded-lg text-sm"
                style={{ backgroundColor: '#fdecec', border: '1px solid #f5b5b5', color: '#8a1f1f' }}>
                <AlertCircle size={16} aria-hidden="true" className="mt-0.5 flex-shrink-0" />
                <span>{submitError}</span>
              </div>
            )}

            <div className="pt-2 flex flex-col items-stretch gap-3">
              <button type="button" onClick={submit} disabled={submitting}
                aria-disabled={submitting}
                className="btn-primary rounded-full w-full py-3 font-semibold inline-flex items-center justify-center gap-2 text-base">
                <Rocket size={18} aria-hidden="true" />
                {submitting ? 'Submitting…' : 'Start My Year 1 Free'}
              </button>
              <p className="text-xs text-center" style={{ color: '#4a4566' }}>
                No credit card required. Your first campaign will be delivered within 48 hours.
              </p>
              <p className="text-xs text-center italic" style={{ color: '#4a4566' }}>
                We never contact your customers during Try it Out. Everything comes to you.
              </p>
              <div className="flex justify-start">
                <BackButton onClick={back} />
              </div>
            </div>
          </fieldset>
        )}
      </div>
      </div>
    </div>
  );
}

function RequiredMark() {
  return <span aria-hidden="true" style={{ color: '#6B4BEF', marginLeft: 2 }}>*</span>;
}

function Field({ id, label, required, error, children }) {
  return (
    <div>
      <label htmlFor={id} className="text-sm font-semibold mb-1.5 block" style={{ color: '#130F28' }}>
        {label}{required && <RequiredMark />}
        {required && <span className="sr-only"> (required)</span>}
      </label>
      {children}
      {error && (
        <p role="alert" className="text-xs mt-1 flex items-center gap-1" style={{ color: '#a31a1a' }}>
          <AlertCircle size={12} aria-hidden="true" /> {error}
        </p>
      )}
    </div>
  );
}

function BackButton({ onClick }) {
  return (
    <button type="button" className="text-sm font-semibold inline-flex items-center gap-2 px-2 py-1 rounded-md focus:outline-none focus:ring-2 focus:ring-[#6B4BEF]"
      style={{ color: '#5132d4' }} onClick={onClick}>
      <ArrowLeft size={16} aria-hidden="true" /> Back
    </button>
  );
}

function Section({ title, onEdit, children }) {
  return (
    <div className="p-4 rounded-xl border" style={{ borderColor: '#e6e2d6', backgroundColor: '#fbfaf6' }}>
      <div className="flex items-center justify-between mb-2">
        <p className="font-semibold text-sm" style={{ color: '#130F28' }}>{title}</p>
        {onEdit && (
          <button type="button" onClick={onEdit}
            className="text-xs font-semibold focus:outline-none focus:ring-2 focus:ring-[#6B4BEF] rounded px-2 py-0.5"
            style={{ color: '#5132d4' }}>Edit</button>
        )}
      </div>
      <div className="space-y-1">{children}</div>
    </div>
  );
}

function Row({ k, v }) {
  return (
    <div className="flex flex-col sm:flex-row sm:justify-between sm:gap-4 gap-0.5 text-sm">
      <span className="text-xs sm:text-sm" style={{ color: '#4a4566' }}>{k}</span>
      <span className="sm:text-right break-words sm:max-w-[60%]" style={{ color: '#130F28' }}>{v}</span>
    </div>
  );
}
